import { createSelector } from '@ngrx/store';
import {
  AccountTypes,
  DecedentInfoState,
  ISelectedClientAccountsState,
  TransactionTypesEnum,
} from '@shared/models';
import { getOpenAccountState, TransactionState } from '@store/reducers';
import { BeneficiariesState } from '@store/reducers/beneficiaries/beneficiaries.reducer';
import { IDocumentUploadState } from '@store/reducers/document-upload/document-upload.reducer';
import { EstateState } from '@store/reducers/estate/estate.reducer';
import { IOpenAccountState } from '@store/reducers/open-account/open-account.reducer';
import { TCFTrustState } from '@store/reducers/tcf-trust/tcf-trust.reducer';
import {
  getIsMultiInheritanceFlow,
  getIsTrustToTrustFlow,
  TriageState,
} from '@store/reducers/triage/triage.reducer';
import { TrustState } from '@store/reducers/trust/trust.reducer';
import { TrustTaxInfoState } from '@store/reducers/trust-tax-info/trust-tax-info.reducer';
import { selectBeneficiaryState } from '@store/selectors/beneficiaries/beneficiaries.selectors';
import {
  selectDecedentFullName,
  selectDecedentInfoState,
} from '@store/selectors/decedent-info/decedent-info.selectors';
import { selectDocumentUploadState } from '@store/selectors/document-upload/document-upload.selectors';
import { selectEstateState } from '@store/selectors/estate/estate.selectors';
import {
  selectedAccountsFrom,
  selectedClientAccountsState,
  selectTransactionType,
} from '@store/selectors/selected-client-accounts/selected-client-accounts.selectors';
import { selectTCFTrustState } from '@store/selectors/tcf-trust/tcf-trust.selectors';
import { selectCurrentTransaction } from '@store/selectors/transaction-state/transaction-state.selectors';
import {
  selectAccountType,
  selectIsProspect,
  selectTriageState,
} from '@store/selectors/triage/triage.selectors';
import { selectTrustState } from '@store/selectors/trust/trust.selectors';
import { selectTrustTaxInfoState } from '@store/selectors/trust-tax-info/trust-tax-info.selector';
import { selectClientPersonalInfoData } from 'vg-interaction-model';
import { IPersonalInfoClientState } from 'vg-interaction-model/dist/lib/features/personal-info-client/store/reducers/personal-info-client.reducer';

import {
  buildAccountFromNonretirement,
  buildAccountToMultiInheritance,
  buildAccountToNonretirement,
} from '../cashier-nonretirement';
import {
  selectEmploymentReceipt,
  selectFinancialProfileReceipt,
  selectProfileReceipt,
} from '../new-account/new-account.selectors';
import {
  buildAccountFromTrust,
  buildAccountToTrust,
  buildDecedentInfoReceipt,
  buildDocumentUploadReceipt,
  buildEstateReceipt,
  buildFundingReceipt,
  buildTrusteeReceipt,
  buildTrustInfoReceipt,
} from './cashier-inheritance';
import {
  buildAccountFromIRA,
  buildAccountToIRA,
  buildBeneByRelationshipReceipt,
  buildDefaultBeneficiaryReceipt,
} from './cashier-ira';

export const selectDecedentInfoReceipt = (editable: boolean) =>
  createSelector(
    selectDecedentInfoState,
    selectTriageState,
    (state: DecedentInfoState, triage: TriageState) =>
      buildDecedentInfoReceipt(state, triage, editable),
  );

export const selectDocumentUploadReceipt = (editable: boolean = true) =>
  createSelector(selectDocumentUploadState, (state: IDocumentUploadState) =>
    buildDocumentUploadReceipt(state, editable),
  );

export const selectEstateReceipt = (editable: boolean) =>
  createSelector(
    selectEstateState,
    selectTriageState,
    (estateState: EstateState, triageState: TriageState) =>
      buildEstateReceipt(estateState, triageState, editable),
  );

export const selectBeneByRelationshipReceipt = (editable: boolean = true) =>
  createSelector(
    selectBeneficiaryState,
    selectTriageState,
    selectClientPersonalInfoData,
    (
      state: BeneficiariesState,
      guide: TriageState,
      personalInfo: IPersonalInfoClientState['clientData'],
    ) => buildBeneByRelationshipReceipt(state, guide, personalInfo, editable),
  );

export const selectAccountFromNonretirementReceipt = (editable: boolean = true) =>
  createSelector(
    selectedAccountsFrom,
    selectAccountType,
    selectTriageState,
    (state, accountType, triageState) =>
      buildAccountFromNonretirement(state, accountType, editable, triageState),
  );

export const selectAccountFromIRAReceipt = (editable: boolean = true) =>
  createSelector(
    selectTriageState,
    selectedClientAccountsState,
    selectIsProspect,
    (guideState, selectedAccount, prospect) =>
      buildAccountFromIRA(guideState, selectedAccount, prospect, editable),
  );

export const selectAccountFromTrustToTrustReceipt = (editable: boolean) =>
  createSelector(selectTCFTrustState, (tcfTrustState) =>
    buildAccountFromTrust(tcfTrustState, editable),
  );

export const selectAccountToIRAReceipt = (editable: boolean) =>
  createSelector(
    selectTriageState,
    selectedClientAccountsState,
    getOpenAccountState,
    (
      guide: TriageState,
      selectedAccountState: ISelectedClientAccountsState,
      openAccountState: IOpenAccountState,
    ) => buildAccountToIRA(guide, selectedAccountState, openAccountState, editable),
  );

export const selectAccountToTrustToTrustReceipt = () => createSelector(() => buildAccountToTrust());

export const selectAccountToNonretirementReceipt = (editable: boolean = true) =>
  createSelector(
    selectClientPersonalInfoData,
    selectedClientAccountsState,
    selectTriageState,
    selectEstateState,
    selectDecedentFullName,
    getOpenAccountState,
    (
      personalInfo: IPersonalInfoClientState['clientData'],
      selectedAccountState: ISelectedClientAccountsState,
      guideState: TriageState,
      estateState: EstateState,
      decedentName: string,
      openAccountState: IOpenAccountState,
    ) => {
      return buildAccountToNonretirement(
        personalInfo,
        selectedAccountState,
        guideState,
        estateState,
        decedentName,
        openAccountState,
        editable,
      );
    },
  );

export const selectAccountToMultiInheritanceReceipt = () =>
  createSelector(selectCurrentTransaction, (currentTransaction: TransactionState | null) => {
    return buildAccountToMultiInheritance(currentTransaction);
  });

export const selectDefaultBeneficiaryReceipt = () =>
  createSelector(
    selectTriageState,
    selectTransactionType,
    selectAccountType,
    (triageState: TriageState, transactionType: TransactionTypesEnum, accountType: AccountTypes) =>
      buildDefaultBeneficiaryReceipt(triageState, transactionType, accountType),
  );

export const selectFundingReceipt = (editable: boolean) =>
  createSelector(
    selectTriageState,
    selectedClientAccountsState,
    (triageState: TriageState, selectedClientAccountsState: ISelectedClientAccountsState) =>
      buildFundingReceipt(triageState, selectedClientAccountsState, editable),
  );

export const getFromAccountData = (editable: boolean) =>
  createSelector(
    selectTriageState,
    selectAccountFromNonretirementReceipt(),
    selectAccountFromIRAReceipt(),
    selectAccountFromTrustToTrustReceipt(editable),
    (triage, from, fromIRA, fromTrustToTrust) => {
      switch (true) {
        case getIsTrustToTrustFlow(triage):
          return fromTrustToTrust;
        case AccountTypes.IRA === triage.accountTypeInheriting:
          return fromIRA;
        default:
          return from;
      }
    },
  );

export const getToAccountData = (editable: boolean) =>
  createSelector(
    selectTriageState,
    selectAccountToNonretirementReceipt(editable),
    selectAccountToIRAReceipt(editable),
    selectAccountToTrustToTrustReceipt(),
    selectAccountToMultiInheritanceReceipt(),
    (triage, to, toIRA, toTrustToTrust, toMultiInheritance) => {
      switch (true) {
        case getIsMultiInheritanceFlow(triage):
          return toMultiInheritance;
        case getIsTrustToTrustFlow(triage):
          return toTrustToTrust;
        case AccountTypes.IRA === triage.accountTypeInheriting:
          return toIRA;
        default:
          return to;
      }
    },
  );

export const getStandardReceiptData = (editable: boolean) =>
  createSelector(
    selectDecedentInfoReceipt(editable),
    selectDocumentUploadReceipt(),
    selectBeneByRelationshipReceipt(),
    getFromAccountData(editable),
    getToAccountData(editable),
    selectEstateReceipt(editable),
    selectProfileReceipt(editable),
    selectFundingReceipt(false),
    selectTrustInfoReceipt(editable),
    selectAddedTrusteeReceipt(editable),
    selectRemovedTrusteeReceipt(editable),
    /* eslint-disable max-params */
    (
      owner,
      documents,
      beneByRelationship,
      from,
      to,
      estate,
      profile,
      funding,
      trustInfo,
      addedTrustee,
      removedTrustee,
    ) => {
      return {
        owner,
        documents,
        beneByRelationship,
        from,
        to,
        trustInfo,
        estate,
        profile,
        addedTrustee,
        removedTrustee,
        funding,
      };
    },
  );

export const getNewAccountReceiptData = createSelector(
  selectEmploymentReceipt(),
  selectFinancialProfileReceipt(),
  selectDefaultBeneficiaryReceipt(),
  (employment, financial, defaultBeneficiary) => {
    return {
      employment,
      financial,
      defaultBeneficiary,
    };
  },
);

export const selectInheritanceReceiptData = (editable: boolean = true) =>
  createSelector(
    getStandardReceiptData(editable),
    getNewAccountReceiptData,
    (standardReceiptData, newAccountReceiptData) => {
      return {
        ...standardReceiptData,
        ...newAccountReceiptData,
      };
    },
  );

export const selectMultiInheritanceNewAccountReceiptData = (editable: boolean = true) =>
  createSelector(
    selectProfileReceipt(editable),
    getToAccountData(editable),
    (profile, accountTo) => {
      return {
        accountTo,
        profile,
      };
    },
  );

export const selectTrustInfoReceipt = (editable: boolean) =>
  createSelector(
    selectTrustState,
    selectTriageState,
    selectTrustTaxInfoState,
    selectTCFTrustState,
    (
      trustInfoState: TrustState,
      triage: TriageState,
      trustTaxInfoState: TrustTaxInfoState,
      tcfTrustState: TCFTrustState,
    ) => buildTrustInfoReceipt(triage, trustInfoState, trustTaxInfoState, tcfTrustState, editable),
  );

export const selectAddedTrusteeReceipt = (editable: boolean) =>
  createSelector(
    selectTriageState,
    selectTCFTrustState,
    (triage: TriageState, tcfTrustState: TCFTrustState) =>
      buildTrusteeReceipt(triage, tcfTrustState, false, editable),
  );

export const selectRemovedTrusteeReceipt = (editable: boolean) =>
  createSelector(
    selectTriageState,
    selectTCFTrustState,
    (triage: TriageState, tcfTrustState: TCFTrustState) =>
      buildTrusteeReceipt(triage, tcfTrustState, true, editable),
  );
